import { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { createReference, getReferenceString } from "@medplum/core";
import { Observation, Practitioner, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import {
  LABOR_AMEND_FLOWSHEET_EDIT_MISSING_RESPONSE_MESSAGE,
  LABOR_AMEND_FLOWSHEET_EDIT_MISSING_RESPONSE_TITLE,
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_EDIT_ROW_MESSAGE,
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
  LABOR_AMEND_QUEUED_MESSAGE,
  LABOR_AMEND_QUICK_ADD_SUCCESS_MESSAGE,
  LABOR_AMEND_QUICK_ADD_SUCCESS_TITLE,
} from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import { QuestionnaireType } from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { LaborQuestionnaireDrawer } from "@/encounters/components/EncounterLabor/EncounterLaborDrawer/LaborQuestionnaireDrawer";
import {
  LABOR_FLOWSHEET_EDIT_MISSING_PREREQUISITES_MESSAGE,
  useLaborFlowsheetEditRouteContext,
} from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/use-labor-flowsheet-edit-route-context";
import { useEnqueueLaborAmendPersist } from "@/encounters/components/EncounterLabor/hooks/use-enqueue-labor-amend-persist";
import { useResolveLaborAmendEncounterParentVersionIdForStamp } from "@/encounters/components/EncounterLabor/hooks/use-labor-amend-encounter-parent-version-id";
import { useLaborQuestionnaireForm } from "@/encounters/components/EncounterLabor/hooks/use-labor-questionnaire-form";
import {
  addQuestionnaireResponseVersioning,
  fetchLaborStageObservations,
  getLaborStageFromMember,
  getQuestionnaire,
  getQuestionnaireResponseFromObservation,
} from "@/encounters/components/EncounterLabor/services/labor";
import { updateLaborFlowsheetQuestionnaireResponse } from "@/encounters/components/EncounterLabor/services/update-labor-flowsheet-questionnaire-response";
import { getLaborFlowsheetEditReplaceKey } from "@/encounters/components/EncounterLabor/utils/labor-amend-pending-keys";
import { formatNormalizedFormValuesForAmendDisplay } from "@/encounters/components/EncounterLabor/utils/labor-amend-preview";
import { buildLaborQuestionnaireSubmitValues } from "@/encounters/components/EncounterLabor/utils/labor-questionnaire-submit-values";
import { dedupeFinalLaborStageObservations } from "@/encounters/components/EncounterLabor/utils/labor-stage-dedupe";
import { useLaborAmendFlowsheetPersist } from "@/encounters/contexts/LaborAmendFlowsheetPersistContext";
import { markQuestionnaireFormAsEditing } from "@/intake-forms/types/questionnaire-form";
import { loadLaborStages } from "@/store/slices/labor-stages";
import { joinReferenceString } from "@/utils/medplum";
import { toast } from "@/utils/notifications";
import { getQuestionnaireResponseFromFormValues } from "@/utils/questionnaires";

interface EditIntakeLaborFlowsheetRowProps {
  observation?: Observation;
  onClose?: () => void;
  questionnaireName: QuestionnaireType;
  questionnaireDisplayName: string | null;
}

/**
 * Legacy intake questionnaire edit drawer for labor flowsheet rows that are not yet on a native drawer.
 * Questionnaire type must be resolved by the caller (`EditLaborRow`).
 */
export default function EditIntakeLaborFlowsheetRow({
  observation,
  onClose,
  questionnaireName,
  questionnaireDisplayName,
}: EditIntakeLaborFlowsheetRowProps) {
  const [opened, setOpened] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialQuestionnaireResponse, setInitialQuestionnaireResponse] = useState<
    QuestionnaireResponse | undefined
  >();
  const { encounterId, clientId, hasRouteContext } = useLaborFlowsheetEditRouteContext(onClose);
  const medplum = useMedplum();
  const practitioner = useMedplumProfile() as Practitioner;
  const dispatch = useDispatch();
  const enqueueAmendPersist = useEnqueueLaborAmendPersist();
  const { deferFlowsheetPersists } = useLaborAmendFlowsheetPersist();
  const resolveEncounterQuestionnaireParentVersionIdForStamp =
    useResolveLaborAmendEncounterParentVersionIdForStamp();

  useEffect(() => {
    const fetchData = async () => {
      if (observation) {
        const response = await getQuestionnaireResponseFromObservation(medplum, observation);
        setInitialQuestionnaireResponse(response);
      }
    };
    fetchData();
  }, [medplum, observation, clientId, encounterId]);

  const {
    isLoading,
    questionnaire,
    questionnaireResponse,
    formSchema,
    form,
    questionnaireError,
    dateTimeUtcDateAnchors,
  } = useLaborQuestionnaireForm({
    questionnaireName,
    encounterId,
    questionnaireResponse: initialQuestionnaireResponse,
    editMode: true,
  });

  useLayoutEffect(() => {
    markQuestionnaireFormAsEditing(form);
  }, [form]);

  const handleClose = () => {
    setOpened(false);
    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (!questionnaire?.id || !formSchema || !practitioner || !encounterId || !clientId) {
      toast({
        type: "error",
        title: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
        message: LABOR_FLOWSHEET_EDIT_MISSING_PREREQUISITES_MESSAGE,
        autoClose: false,
      });
      handleClose();
      return;
    }

    setIsSubmitting(true);
    try {
      const normalizedValues = buildLaborQuestionnaireSubmitValues({
        form,
        formSchema,
        questionnaire,
        dateTimeUtcDateAnchors,
      });
      if (!normalizedValues) {
        return;
      }

      if (!initialQuestionnaireResponse) {
        toast({
          type: "error",
          title: LABOR_AMEND_FLOWSHEET_EDIT_MISSING_RESPONSE_TITLE,
          message: LABOR_AMEND_FLOWSHEET_EDIT_MISSING_RESPONSE_MESSAGE,
          autoClose: false,
        });
        handleClose();
        return;
      }

      const patientReference = { reference: joinReferenceString("Patient", clientId) };
      const practitionerReference = createReference(practitioner);

      const performPersist = async () => {
        const questionnaireResource = await getQuestionnaire(medplum, questionnaireName);
        if (!questionnaireResource) {
          throw new Error(`Questionnaire ${questionnaireName} not found.`);
        }

        const questionnaireResponseToUpdate =
          (observation
            ? await getQuestionnaireResponseFromObservation(medplum, observation)
            : undefined) ?? initialQuestionnaireResponse;

        if (!questionnaireResponseToUpdate) {
          throw new Error("No existing questionnaire response found to update");
        }

        const currentLaborStageRef = await getLaborStageFromMember(
          medplum,
          observation,
          clientId,
          encounterId,
        );

        const authorReference = practitioner.id ? getReferenceString(practitioner) : undefined;

        const versionedQuestionnaireResponse = addQuestionnaireResponseVersioning(
          {
            ...questionnaireResponseToUpdate,
            item: getQuestionnaireResponseFromFormValues(
              questionnaireResource,
              normalizedValues,
              patientReference,
              practitionerReference,
            ).item,
            authored: new Date().toISOString(),
          },
          questionnaireResponseToUpdate,
          observation,
          authorReference,
        );

        await updateLaborFlowsheetQuestionnaireResponse(medplum, {
          questionnaireResponse: versionedQuestionnaireResponse,
          laborStagePartOf: currentLaborStageRef,
          encounterQuestionnaireParentVersionId:
            resolveEncounterQuestionnaireParentVersionIdForStamp(),
        });

        if (!deferFlowsheetPersists) {
          await new Promise((resolve) => setTimeout(resolve, 1000));

          const observations = await fetchLaborStageObservations(medplum, clientId, encounterId);
          dispatch(loadLaborStages(dedupeFinalLaborStageObservations(observations)));

          toast({
            type: "success",
            title: LABOR_AMEND_QUICK_ADD_SUCCESS_TITLE,
            message: LABOR_AMEND_QUICK_ADD_SUCCESS_MESSAGE,
            autoClose: 3000,
          });
        }
      };

      const replaceKey = observation?.id
        ? getLaborFlowsheetEditReplaceKey(observation.id)
        : undefined;
      if (
        enqueueAmendPersist({
          replaceKey,
          pendingDisplayEntry: observation?.id
            ? {
                key: replaceKey!,
                title: questionnaireDisplayName ?? "Labor flowsheet",
                details: formatNormalizedFormValuesForAmendDisplay(
                  normalizedValues as Record<string, unknown>,
                ),
              }
            : undefined,
          persist: performPersist,
          queuedMessage: LABOR_AMEND_QUEUED_MESSAGE,
          errorTitle: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
          errorMessage: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_EDIT_ROW_MESSAGE,
          onQueued: handleClose,
        })
      ) {
        return;
      }

      await performPersist();
      handleClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        type: "error",
        title: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
        message:
          error instanceof Error
            ? error.message
            : LABOR_AMEND_FLOWSHEET_SAVE_ERROR_EDIT_ROW_MESSAGE,
        autoClose: false,
      });
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasRouteContext) {
    return null;
  }

  return (
    <LaborQuestionnaireDrawer
      form={form}
      opened={opened}
      onClose={handleClose}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      submitButtonText="Update"
      error={questionnaireError}
      isSubmitting={isSubmitting}
      questionnaire={questionnaire}
      title={questionnaireDisplayName || ""}
      questionnaireResponse={questionnaireResponse}
      dateTimeUtcDateAnchors={dateTimeUtcDateAnchors}
      formId={`labor-flowsheet-form-${questionnaireName}`}
    />
  );
}
